import { createClient } from '@supabase/supabase-js'
import type { Database } from './database.types'

/**
 * Read Vite env only. Do not touch `process.env` as an object in browser code —
 * bundlers can inline the entire Hostinger build environment into the client JS.
 */
const getEnvVar = (key: string): string | undefined => {
  const viteValue = import.meta.env[key]
  if (typeof viteValue === 'string' && viteValue.length > 0) {
    return viteValue.trim()
  }
  return undefined
}

/** JWT anon keys are case-sensitive; Hostinger has previously uppercased env values. */
function assertUsableAnonKey(key: string): void {
  const letters = key.replace(/[^A-Za-z]/g, '')
  if (letters.length > 0 && letters === letters.toUpperCase()) {
    throw new Error(
      'VITE_SUPABASE_ANON_KEY looks fully uppercased. Supabase JWT keys are case-sensitive. ' +
        'In Hostinger → website Environment variables, re-paste the anon key exactly as shown in ' +
        'Supabase → Project Settings → API (mixed case), then Redeploy.'
    )
  }
}

// Lazy initialization function
let supabaseClient: ReturnType<typeof createClient<Database>> | null = null;

function initializeSupabase() {
  if (supabaseClient) {
    return supabaseClient;
  }

  const supabaseUrl = getEnvVar('VITE_SUPABASE_URL')
  const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY')

  // Node/tsx contract tests only — never read process.env in a way that inlines it for browsers
  const isTestMode =
    typeof window === 'undefined' &&
    typeof process !== 'undefined' &&
    process.env['RUN_SERVER'] === 'true'
  
  if (!supabaseUrl || !supabaseAnonKey) {
    if (isTestMode) {
      // In test mode, create a dummy client that will fail gracefully
      // This allows the server to start even without Supabase credentials
      console.warn('⚠️  Supabase credentials not found. Server will run in test mode without database.');
      // Create a client with dummy values - it won't work but won't crash
      supabaseClient = createClient<Database>('https://dummy.supabase.co', 'dummy-key', {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
        }
      });
      return supabaseClient;
    }
    
    // Provide helpful error message with instructions
    const isBrowser = typeof window !== 'undefined';
    const missingVars = [];
    if (!supabaseUrl) missingVars.push('VITE_SUPABASE_URL');
    if (!supabaseAnonKey) missingVars.push('VITE_SUPABASE_ANON_KEY');
    
    const errorMsg = 
      `Missing Supabase environment variables: ${missingVars.join(', ')}\n\n` +
      `To fix this:\n` +
      `1. Check your .env file in the project root\n` +
      `2. Ensure it contains:\n` +
      `   VITE_SUPABASE_URL=https://your-project-id.supabase.co\n` +
      `   VITE_SUPABASE_ANON_KEY=your-anon-key\n` +
      `3. ${isBrowser ? 'Restart your Vite dev server (stop and run `npm run dev` again)' : 'Restart the application'}\n` +
      `   (Vite loads .env variables at startup, so changes require a restart)`;
    
    throw new Error(errorMsg);
  }

  assertUsableAnonKey(supabaseAnonKey)

  // Create typed Supabase client (singleton pattern)
  // This ensures only one client instance is created, preventing multiple GoTrueClient warnings
  // Using a unique storage key to avoid conflicts with other Supabase instances
  supabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      // Configure auth settings
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      // Use a unique storage key to avoid conflicts
      storageKey: 'moneymojo-auth',
    },
    // Configure realtime settings
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  });

  return supabaseClient;
}

// Re-export Database type for convenience
export type { Database }

// Export lazy-initialized client using Proxy to maintain API compatibility
export const supabase = new Proxy({} as ReturnType<typeof createClient<Database>>, {
  get(_target, prop) {
    const client = initializeSupabase();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  }
})

// Export typed client as an alias for backward compatibility
// Use 'supabase' directly - it's now typed and is the single source of truth
export const typedSupabase = supabase

/**
 * Get current authenticated user ID
 * 
 * @returns Promise<string | null> - The authenticated user ID, or null if not authenticated
 */
export async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error || !user) {
      return null;
    }

    return user.id;
  } catch (error) {
    // Use safe error logging in production
    if (import.meta.env.PROD) {
      // In production, don't log sensitive error details
      return null;
    }
    console.error('Error getting current user ID:', error);
    return null;
  }
}
